import { useMemo, useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import { cn } from "@/src/lib/utils";
import { Booking } from "../../model";
import { useBookingCommissionMutation } from "@/src/lib/bookingCommissionMutation";

/**
 * HELM commission (brief §5): percentage or keyed amount, showing what the app
 * assumed against what's on the contract, and the net charter fee that goes to
 * HubSpot on save.
 */

const STANDARD_PCT = 15;

const fmt = (v: number | null | undefined, ccy?: string | null) =>
  v == null ? "—" : new Intl.NumberFormat("en-EN", { style: "currency", currency: ccy || "EUR", maximumFractionDigits: 0 }).format(v);

export function CommissionSection({ booking }: { booking: Booking }) {
  const mutation = useBookingCommissionMutation(booking.id);

  const basis = useMemo(() => {
    const contractFee = Number(booking.convertDetails?.financials?.charterFee) || 0;
    // Booking total, not clientPrice — that's the base the net charter fee has
    // always been maintained against (clientPrice excludes extras, which makes
    // the derived commission go negative on real bookings).
    const totalPrice = Number((booking.pricing as any)?.totalPrice) || 0;
    const appFee = totalPrice > 0 ? totalPrice : Number((booking.pricing as any)?.clientPrice) || 0;
    return {
      fee: contractFee > 0 ? contractFee : appFee,
      source: contractFee > 0 ? "contract" : "offer",
      contractFee: contractFee || null,
      appFee: appFee || null,
      currency: booking.convertDetails?.financials?.currency || booking.netCharterFeeCurrency || booking.currency?.code,
    };
  }, [booking]);

  const [mode, setMode] = useState<"pct" | "amount">(booking.commissionAmount != null && booking.commissionPct == null ? "amount" : "pct");
  const [pct, setPct] = useState(String(booking.commissionPct ?? STANDARD_PCT));
  const [amount, setAmount] = useState(String(booking.commissionAmount ?? Math.round((basis.fee * STANDARD_PCT) / 100)));

  const currentAmount = mode === "pct" ? (basis.fee * (Number(pct) || 0)) / 100 : Number(amount) || 0;
  const currentPct = basis.fee > 0 ? (currentAmount / basis.fee) * 100 : 0;
  const net = basis.fee - currentAmount;
  const assumed = (basis.fee * STANDARD_PCT) / 100;
  const overridden = Math.abs(currentPct - STANDARD_PCT) > 0.05;
  const feesDiffer = basis.contractFee != null && basis.appFee != null && Math.abs(basis.contractFee - basis.appFee) > 1;

  // Net fee already on record (imported from HubSpot for historical bookings).
  // It only implies a commission when it sits sanely inside the charter fee —
  // on real data 13% exceed the total and 11% imply over 35%, so those are
  // shown as "doesn't reconcile" rather than printed as a bogus percentage.
  const recorded = booking.netCharterFee != null ? Number(booking.netCharterFee) : null;
  const recordedPct =
    recorded != null && basis.fee > 0 && recorded <= basis.fee && (basis.fee - recorded) / basis.fee <= 0.35
      ? ((basis.fee - recorded) / basis.fee) * 100
      : null;

  if (basis.fee <= 0) {
    return (
      <Card className="w-full">
        <h3 className="text-lg font-semibold">HELM commission</h3>
        <p className="mt-2 text-sm text-slate-500">No charter fee on this booking yet — commission can't be calculated.</p>
      </Card>
    );
  }

  return (
    <Card className="flex w-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">HELM commission</h3>
          <p className="text-xs text-slate-500">
            Taken off the {basis.source === "contract" ? "contract" : "offer"} charter fee · saving writes the net
            charter fee to HubSpot
          </p>
        </div>
        <Button
          variant="link"
          onClick={() => {
            setMode("pct");
            setPct(String(STANDARD_PCT));
          }}
        >
          Reset to standard {STANDARD_PCT}%
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex rounded-md border p-0.5">
          {(["pct", "amount"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={cn(
                "rounded px-3 py-1 text-sm",
                mode === m ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100",
              )}
            >
              {m === "pct" ? "Percentage" : "Keyed amount"}
            </button>
          ))}
        </div>
        {mode === "pct" ? (
          <label className="text-xs text-slate-500">
            Commission %
            <Input type="number" step="0.5" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1 w-32" />
          </label>
        ) : (
          <label className="text-xs text-slate-500">
            Commission amount ({basis.currency})
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-40" />
          </label>
        )}
        <Button
          disabled={mutation.isPending}
          onClick={() =>
            mutation.mutate(
              mode === "pct"
                ? { mode: "pct", commissionPct: Number(pct) }
                : { mode: "amount", commissionAmount: Number(amount) },
            )
          }
        >
          {mutation.isPending ? "Saving…" : "Save commission"}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-3 sm:grid-cols-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Charter fee</p>
          <p className="text-base font-semibold tabular-nums">{fmt(basis.fee, basis.currency)}</p>
          <p className="text-[11px] text-slate-400">from the {basis.source}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Commission</p>
          <p className="text-base font-semibold tabular-nums">{fmt(currentAmount, basis.currency)}</p>
          <p className="text-[11px] text-slate-400">{currentPct.toFixed(1)}%</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Net charter fee → HubSpot</p>
          <p className="text-base font-semibold tabular-nums text-slate-900">{fmt(net, basis.currency)}</p>
          <p className="text-[11px] text-slate-400">
            {recorded != null
              ? `on record: ${fmt(recorded, booking.netCharterFeeCurrency ?? basis.currency)}${
                  recordedPct != null ? ` · implies ${recordedPct.toFixed(1)}%` : " · doesn't reconcile"
                }`
              : "nothing on record yet"}
          </p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400">App assumed</p>
          <p className="text-base font-semibold tabular-nums text-slate-600">{fmt(assumed, basis.currency)}</p>
          <p className="text-[11px] text-slate-400">standard {STANDARD_PCT}%</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 font-medium",
            overridden ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800",
          )}
        >
          {overridden ? "Overridden — differs from standard" : "Standard"}
        </span>
        {feesDiffer && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
            Contract fee {fmt(basis.contractFee, basis.currency)} vs offer {fmt(basis.appFee, basis.currency)} — check which is right
          </span>
        )}
        <span className="text-slate-400">
          The contract's own commission figure isn't parsed, so only the fee is compared.
        </span>
      </div>
    </Card>
  );
}
